import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Users, FileWarning, Activity, RefreshCw, Lock, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="border border-zinc-200 p-4" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-semibold tracking-[0.06em] uppercase text-zinc-400">{label}</p>
          <p className={`font-heading text-2xl font-bold mt-1 ${color || 'text-zinc-900'}`}>{value}</p>
        </div>
        <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-sm">
          <Icon size={16} strokeWidth={1.5} className="text-zinc-600" />
        </div>
      </div>
    </div>
  );
}

export default function SecurityDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/dashboard/security");
      setData(d);
    } catch(e) { console.error("Load error:", e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="flex items-center justify-center py-20 text-zinc-400 text-sm" data-testid="security-loading">Loading security data...</div>;

  const { summary, failed_logins, active_sessions, recent_audit, upload_rejections } = data;

  return (
    <div data-testid="security-dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Security Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">Real-time security monitoring</p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-sm h-8 text-xs" data-testid="security-refresh">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="security-summary">
        <StatCard icon={XCircle} label="Failed Logins (24h)" value={summary.failed_logins_24h} color="text-red-600" testId="stat-failed-logins" />
        <StatCard icon={CheckCircle} label="Successful Logins (24h)" value={summary.successful_logins_24h} color="text-green-600" testId="stat-success-logins" />
        <StatCard icon={Users} label="Active Sessions" value={summary.active_sessions} testId="stat-active-sessions" />
        <StatCard icon={AlertTriangle} label="Server Errors (24h)" value={summary.server_errors_24h} color="text-amber-600" testId="stat-server-errors" />
        <StatCard icon={Lock} label="Locked Accounts" value={summary.locked_accounts} color={summary.locked_accounts > 0 ? "text-red-600" : "text-zinc-900"} testId="stat-locked-accounts" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Failed Login Attempts */}
        <div className="border border-zinc-200" data-testid="failed-logins-widget">
          <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center gap-2">
            <ShieldAlert size={14} className="text-red-500" />
            <h3 className="text-xs font-semibold text-zinc-900">Failed Login Attempts (7 days)</h3>
            <span className="ml-auto text-[10px] text-zinc-400">{failed_logins.length} events</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {failed_logins.length === 0 ? (
              <p className="p-4 text-xs text-zinc-400 text-center">No failed login attempts</p>
            ) : failed_logins.map((fl, i) => (
              <div key={i} className="px-4 py-2 border-b border-zinc-50 flex items-center justify-between" data-testid={`failed-login-${i}`}>
                <div>
                  <p className="text-xs font-medium text-zinc-900">{fl.entity_id || fl.new_value || "Unknown"}</p>
                  <p className="text-[10px] text-zinc-400">{fmtDateTime(fl.timestamp)}</p>
                </div>
                <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded-sm bg-red-100 text-red-700">FAILED</span>
              </div>
            ))}
          </div>
        </div>

        {/* Active Sessions */}
        <div className="border border-zinc-200" data-testid="active-sessions-widget">
          <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center gap-2">
            <Users size={14} className="text-green-600" />
            <h3 className="text-xs font-semibold text-zinc-900">Active Sessions</h3>
            <span className="ml-auto text-[10px] text-zinc-400">{active_sessions.length} users</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {active_sessions.length === 0 ? (
              <p className="p-4 text-xs text-zinc-400 text-center">No active sessions</p>
            ) : active_sessions.map((s, i) => (
              <div key={i} className="px-4 py-2 border-b border-zinc-50 flex items-center justify-between" data-testid={`active-session-${i}`}>
                <div>
                  <p className="text-xs font-medium text-zinc-900">{s.name}</p>
                  <p className="text-[10px] text-zinc-500">{s.email}</p>
                </div>
                <div className="text-right">
                  <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded-sm bg-zinc-100 text-zinc-600">{(s.roles || [])[0]}</span>
                  <p className="text-[10px] text-zinc-400 mt-0.5">{fmtDateTime(s.last_login)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Audit Events */}
        <div className="border border-zinc-200" data-testid="audit-events-widget">
          <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center gap-2">
            <Activity size={14} className="text-blue-600" />
            <h3 className="text-xs font-semibold text-zinc-900">Recent Audit Events (24h)</h3>
            <span className="ml-auto text-[10px] text-zinc-400">{recent_audit.length} events</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {recent_audit.length === 0 ? (
              <p className="p-4 text-xs text-zinc-400 text-center">No recent events</p>
            ) : recent_audit.map((ae, i) => (
              <div key={i} className="px-4 py-2 border-b border-zinc-50" data-testid={`audit-event-${i}`}>
                <div className="flex items-center justify-between">
                  <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${
                    ae.action === "login" ? "bg-green-100 text-green-700" :
                    ae.action === "login_failed" ? "bg-red-100 text-red-700" :
                    ae.action === "server_error_500" ? "bg-red-100 text-red-700" :
                    ae.action?.includes("unauthorized") ? "bg-amber-100 text-amber-700" :
                    "bg-zinc-100 text-zinc-600"
                  }`}>{ae.action}</span>
                  <span className="text-[10px] text-zinc-400">{fmtDateTime(ae.timestamp)}</span>
                </div>
                <p className="text-xs text-zinc-700 mt-0.5">{ae.user_name || "system"} — {ae.entity_type}/{ae.entity_id || "—"}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Upload Rejection Log */}
        <div className="border border-zinc-200" data-testid="upload-rejections-widget">
          <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center gap-2">
            <FileWarning size={14} className="text-amber-500" />
            <h3 className="text-xs font-semibold text-zinc-900">Upload Rejections (7 days)</h3>
            <span className="ml-auto text-[10px] text-zinc-400">{upload_rejections.length} rejections</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {upload_rejections.length === 0 ? (
              <p className="p-4 text-xs text-zinc-400 text-center">No upload rejections</p>
            ) : upload_rejections.map((ur, i) => (
              <div key={i} className="px-4 py-2 border-b border-zinc-50" data-testid={`upload-rejection-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-zinc-900">{ur.user_name || "Unknown"}</p>
                  <span className="text-[10px] text-zinc-400">{fmtDateTime(ur.timestamp)}</span>
                </div>
                <p className="text-[10px] text-amber-700 mt-0.5">{ur.new_value || "File rejected"}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
